import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { McPill } from "../../shared/McPill"

const COPY: Record<
  ReplyPendingKind,
  { label: TranslationKeys; tooltip: TranslationKeys }
> = {
  reply: {
    label: "MC_INQ_REPLY_PENDING_PILL",
    tooltip: "MC_INQ_REPLY_PENDING_TOOLTIP",
  },
  message: {
    label: "MC_INQ_OPEN_MESSAGE_PILL",
    tooltip: "MC_INQ_OPEN_MESSAGE_TOOLTIP",
  },
}

/** `reply`: a reply arrived but the status still says waiting. `message`: a supplier
 *  reply brought nothing new and nobody answered it or marked it done (TUR-1754). */
export type ReplyPendingKind = "reply" | "message"

interface ReplyPendingPillProps {
  kind?: ReplyPendingKind
  className?: string
}

/** A supplier reply arrived but the inquiry is still "sent · waiting": only a
 *  linked declaration moves the status, so a text-only reply, an unreadable
 *  attachment or a held document would otherwise be visible in the detail
 *  timeline alone. Pill names the state; the Tooltip says what to do. Same
 *  trigger shape as RoundtripHoldPill (a real DOM ref for Radix). */
export function ReplyPendingPill({
  kind = "reply",
  className,
}: ReplyPendingPillProps) {
  const { translate } = useTranslation()
  const copy = COPY[kind]

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            className={cn("inline-flex cursor-default", className)}
          >
            <McPill tone="attention">{translate(copy.label)}</McPill>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[280px]">
          {translate(copy.tooltip)}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/** The one pill a row or header shows for its supplier replies: an open message
 *  says more than "reply pending", so it wins when both apply. */
export function replyPendingKind(inquiry: {
  hasOpenSupplierMessage?: boolean
  hasUnprocessedReply: boolean
}): ReplyPendingKind | null {
  if (inquiry.hasOpenSupplierMessage) return "message"
  return inquiry.hasUnprocessedReply ? "reply" : null
}
